import { Controller } from '@hotwired/stimulus'
import { createPopper } from '@popperjs/core'
import '../../assets/stylesheets/components/bot_function_autocomplete.css'

// Insert an object reference (@[gid|name]) into the SOP editor via an @-triggered autocomplete. Modeled on
// sop_bot_function_autocomplete_controller — same dropdown/popper/keyboard machinery — but sourced from
// /ai/content_references (forms, articles, SOPs) and inserting a reference token the backend resolves
// (ContentReference / ContentReferenceResolver). Browser-only; verify manually in the SOP editor.
const MAX_FILTER_LENGTH = 50
const EDITOR_RETRY_INTERVAL = 200
const EDITOR_MAX_RETRIES = 10
// Forms, knowledge articles, and SOPs. Keep in sync with ContentReference::ALLOWED_TYPES.
const TYPES = ['form', 'article', 'sop']
// Display labels per reference type (SOP is an acronym, not "Sop").
const TYPE_LABELS = { form: 'Form', article: 'Article', sop: 'SOP' }

export default class extends Controller {
  static values = { trigger: { type: String, default: '@' } }

  connect() {
    this.cache = null
    this.dropdown = null
    this.searchInput = null
    this.resultsContainer = null
    this.popperInstance = null
    this.anchorOffset = null
    this.selectedIndex = 0
    this.items = []

    this.handleClickOutside = this.handleClickOutside.bind(this)
    this.handleEditorScroll = this.handleEditorScroll.bind(this)

    this.fetchToken = 0
    this.fetchReferences()
    setTimeout(() => this.setupListener(), 100)
  }

  disconnect() {
    this.destroyDropdown()
    if (this.editorElement) {
      this.editorElement.removeEventListener('keydown', this.handleKeydown, true)
      this.editorElement.removeEventListener('scroll', this.handleEditorScroll)
    }
    document.removeEventListener('mousedown', this.handleClickOutside)
  }

  // --- Editor setup ---

  setupListener(retries = 0) {
    this.editor = this.element.editorInstance
    this.editorElement = this.element.nextElementSibling?.querySelector('.toastui-editor-contents[contenteditable="true"]')
    if (!this.editor || !this.editorElement) {
      if (retries < EDITOR_MAX_RETRIES) setTimeout(() => this.setupListener(retries + 1), EDITOR_RETRY_INTERVAL)
      return
    }
    this.handleKeydown = this.handleKeydown.bind(this)
    this.editorElement.addEventListener('keydown', this.handleKeydown, true)
  }

  // The endpoint returns at most 20 rows per type. A knowledge base is larger than that, so a typed
  // query is sent to the server instead of filtering a first page that may not contain the article.
  scheduleSearch() {
    this.selectedIndex = 0
    clearTimeout(this.searchTimer)
    this.searchTimer = setTimeout(async () => {
      await this.fetchReferences(this.searchInput?.value || '')
      this.selectedIndex = 0
      this.renderItems()
    }, 150)
  }

  async fetchReferences(query = '') {
    const token = ++this.fetchToken
    const q = encodeURIComponent(query)
    try {
      const responses = await Promise.all(
        TYPES.map(type => fetch(`/ai/content_references?type=${type}&q=${q}`, { headers: { Accept: 'application/json' } }))
      )
      const groups = await Promise.all(responses.map(r => (r.ok ? r.json() : [])))
      if (token !== this.fetchToken) return
      this.cache = groups.flat()
    } catch (error) {
      if (token !== this.fetchToken) return
      console.error('Failed to fetch content references:', error)
      this.cache = []
    }
  }

  // --- Keyboard handling ---

  async handleKeydown(event) {
    if (event.key === this.triggerValue && !this.isDropdownVisible()) {
      // Only treat "@" as a reference trigger at a word boundary (start of line / after whitespace). Typed
      // mid-word — e.g. an email like support@brand.com — let it through as a literal "@".
      if (!this.atWordBoundary()) return
      event.preventDefault()
      if (!this.cache) await this.fetchReferences()
      this.selectedIndex = 0
      this.showDropdown()
    }
  }

  // True when the caret sits at the start of a text run or immediately after whitespace, i.e. where a
  // reference (rather than a literal "@") is intended.
  atWordBoundary() {
    const selection = window.getSelection()
    if (!selection || !selection.rangeCount) return true

    const range = selection.getRangeAt(0)
    if (!range.collapsed) return false

    const node = range.startContainer
    if (node.nodeType !== Node.TEXT_NODE) return true // element boundary (e.g. empty line) → start of run

    const charBefore = node.textContent.charAt(range.startOffset - 1)
    return charBefore === '' || /\s/.test(charBefore)
  }

  handleSearchKeydown(event) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        this.navigate(1)
        return
      case 'ArrowUp':
        event.preventDefault()
        this.navigate(-1)
        return
      case 'Enter':
      case 'Tab':
        event.preventDefault()
        this.selectCurrent()
        return
      case 'Escape':
        event.preventDefault()
        this.hideDropdown()
        return
      case 'Backspace':
        if (this.searchInput.value === '') {
          event.preventDefault()
          this.hideDropdown()
        }
    }
  }

  // --- Dropdown lifecycle ---

  showDropdown() {
    this.captureAnchorOffset()
    this.ensureDropdownElement()
    this.searchInput.value = ''
    this.dropdown.style.display = 'flex'
    this.fetchReferences('').then(() => this.renderItems())
    this.renderItems()
    this.createPopper()
    this.searchInput.focus()
    document.addEventListener('mousedown', this.handleClickOutside)
    this.editorElement?.addEventListener('scroll', this.handleEditorScroll)
  }

  hideDropdown() {
    this.destroyPopper()
    if (this.dropdown) this.dropdown.style.display = 'none'
    this.selectedIndex = 0
    this.editorElement?.focus()
    document.removeEventListener('mousedown', this.handleClickOutside)
    this.editorElement?.removeEventListener('scroll', this.handleEditorScroll)
  }

  destroyDropdown() {
    this.destroyPopper()
    if (this.dropdown) {
      this.dropdown.remove()
      this.dropdown = null
      this.searchInput = null
      this.resultsContainer = null
    }
  }

  isDropdownVisible() {
    return this.dropdown && this.dropdown.style.display !== 'none'
  }

  ensureDropdownElement() {
    if (this.dropdown) return

    this.dropdown = document.createElement('div')
    this.dropdown.className = 'bot-function-autocomplete'

    const searchBar = document.createElement('div')
    searchBar.className = 'bot-function-autocomplete__search'

    searchBar.innerHTML = `<svg class="bot-function-autocomplete__search-icon" viewBox="0 0 20 20" fill="currentColor">
      <path fill-rule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clip-rule="evenodd"/>
    </svg>`

    this.searchInput = document.createElement('input')
    this.searchInput.className = 'bot-function-autocomplete__search-input'
    this.searchInput.type = 'text'
    this.searchInput.placeholder = 'Reference a form, article, or SOP...'
    this.searchInput.setAttribute('autocomplete', 'off')
    this.searchInput.maxLength = MAX_FILTER_LENGTH
    this.searchInput.addEventListener('input', () => this.scheduleSearch())
    this.searchInput.addEventListener('keydown', e => this.handleSearchKeydown(e))

    searchBar.appendChild(this.searchInput)
    this.dropdown.appendChild(searchBar)

    this.resultsContainer = document.createElement('div')
    this.resultsContainer.className = 'bot-function-autocomplete__results'
    this.resultsContainer.addEventListener('mousedown', event => {
      event.preventDefault()
      event.stopPropagation()
      const item = event.target.closest('.function-item')
      if (item) {
        this.selectedIndex = parseInt(item.dataset.index)
        this.selectCurrent()
      }
    })
    this.dropdown.appendChild(this.resultsContainer)

    document.body.appendChild(this.dropdown)
  }

  handleClickOutside(event) {
    if (this.isDropdownVisible() && !this.dropdown.contains(event.target)) this.hideDropdown()
  }

  // --- Popper positioning (identical to the bot-function autocomplete) ---

  createPopper() {
    this.destroyPopper()
    const self = this
    const virtualElement = {
      getBoundingClientRect() {
        const editorRect = self.editorElement?.getBoundingClientRect()
        if (!editorRect || !self.anchorOffset) {
          return { top: 0, right: 0, bottom: 0, left: 0, width: 0, height: 0, x: 0, y: 0 }
        }
        const top = editorRect.top + self.anchorOffset.top - self.editorElement.scrollTop
        const left = editorRect.left + self.anchorOffset.left - self.editorElement.scrollLeft
        const height = self.anchorOffset.height
        return { top, right: left, bottom: top + height, left, width: 0, height, x: left, y: top }
      }
    }
    this.popperInstance = createPopper(virtualElement, this.dropdown, {
      placement: 'bottom-start',
      strategy: 'fixed',
      modifiers: [
        { name: 'offset', options: { offset: [0, 4] } },
        { name: 'flip', options: { fallbackPlacements: ['top-start'] } },
        { name: 'preventOverflow', options: { padding: 16 } }
      ]
    })
  }

  destroyPopper() {
    if (this.popperInstance) {
      this.popperInstance.destroy()
      this.popperInstance = null
    }
  }

  captureAnchorOffset() {
    const cursorRect = this.getCursorViewportRect()
    const editorRect = this.editorElement?.getBoundingClientRect()
    if (cursorRect && editorRect) {
      this.anchorOffset = {
        top: cursorRect.top - editorRect.top + this.editorElement.scrollTop,
        left: cursorRect.left - editorRect.left + this.editorElement.scrollLeft,
        height: cursorRect.height
      }
    } else {
      this.anchorOffset = { top: 0, left: 0, height: 0 }
    }
  }

  getCursorViewportRect() {
    const selection = window.getSelection()
    if (!selection.rangeCount) return null
    const range = selection.getRangeAt(0)
    let rect = range.getBoundingClientRect()
    if (rect.width === 0 && rect.height === 0 && range.collapsed) {
      const span = document.createElement('span')
      span.textContent = '​'
      range.insertNode(span)
      rect = span.getBoundingClientRect()
      span.parentNode.removeChild(span)
      selection.removeAllRanges()
      selection.addRange(range)
    }
    return rect.top > 0 || rect.left > 0 ? rect : null
  }

  handleEditorScroll() {
    this.popperInstance?.update()
  }

  // --- Rendering ---

  renderItems() {
    if (!this.cache || !this.resultsContainer) return

    this.items = this.filterReferences(this.searchInput?.value || '')
    if (this.items.length === 0) {
      this.resultsContainer.innerHTML = '<div class="function-item-empty">No matching references</div>'
      return
    }
    this.resultsContainer.innerHTML = this.items.map((ref, i) => this.renderItem(ref, i)).join('')
  }

  renderItem(ref, index) {
    const selected = index === this.selectedIndex ? 'selected' : ''
    return `
      <div class="function-item ${selected}" data-index="${index}">
        <div class="function-item__body">
          <div class="function-item__header">
            <span class="function-item__name">${this.escapeHtml(ref.name)}</span>
            <span class="function-item__category">${this.escapeHtml(TYPE_LABELS[ref.type] || ref.type)}</span>
          </div>
        </div>
      </div>`
  }

  filterReferences(filterText) {
    if (!this.cache) return []
    if (filterText === '') return this.cache
    const tokens = filterText.toLowerCase().split(/\s+/).filter(Boolean)
    return this.cache.filter(ref => {
      const searchable = `${ref.name} ${ref.type}`.toLowerCase()
      return tokens.every(token => searchable.includes(token))
    })
  }

  navigate(direction) {
    const len = this.items?.length
    if (!len) return
    this.selectedIndex = (this.selectedIndex + direction + len) % len
    this.renderItems()
  }

  selectCurrent() {
    const ref = this.items?.[this.selectedIndex]
    if (ref) this.insertReference(ref)
    this.hideDropdown()
  }

  insertReference(ref) {
    if (!this.editor || !this.editorElement) return

    // Sanitize the name so it can't break the token (no | or ] inside).
    const name = String(ref.name || '').replace(/[|\]]/g, ' ')
    this.editor.insertText(`@[${ref.gid}|${name}] `)
    this.editorElement.focus()

    // Convert the just-inserted token into a chip now — the same conversion markdown_editor_controller
    // runs on load — rather than relying on a fragile inline widget-node insertion (it otherwise only
    // rendered after a page refresh). Deferred a tick so insertText has fully applied to the editor state.
    setTimeout(() => this.element.dispatchEvent(new CustomEvent('markdown-editor:convert-references')), 0)
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}
